import iconRising from './../../images/success/icon-rising.svg';
import iconCredit from './../../images/success/icon-credit.svg';
import iconValue from './../../images/success/icon-value.svg';

const baseData = {
  cash: {
    solar: {
      net_savings: 0,
      out_of_pocket: 0,
      system_cost: 0,
      payback_period: 0,
      itc: 0,
      monthly_cost: 0,
      savings25Years: 0,
    },
    solar_battery: {
      net_savings: 0,
      out_of_pocket: 0,
      system_cost: 0,
      payback_period: 0,
      itc: 0,
      monthly_cost: 0,
      savings25Years: 0,
    },
  },
  loan: {
    solar: {
      net_savings: 0,
      out_of_pocket: 0,
      system_cost: 0,
      payback_period: 0,
      itc: 0,
      monthly_cost: 0,
      savings25Years: 0,
    },
    solar_battery: {
      net_savings: 0,
      out_of_pocket: 0,
      system_cost: 0,
      payback_period: 0,
      itc: 0,
      monthly_cost: 0,
      savings25Years: 0,
    },
  },
  system_details: {
    pv_size: 0,
    pv_size_battery: 0,
    panel_count: 0,
    panel_count_battery: 0,
    battery_size: 0,
    monthly_usage_kw: 0,
    traffif_id: 0,
  },
};

export const initialData = {
  ...baseData,
  nem3: { ...baseData },
};

export const goSolarReasonsEnphase = [
  {
    title: 'Electric bills are rising',
    description:
      'The average residential electricity rate in United States is projected to increase 2.5% in 2023 over and above a 10% increase in 2022.¹ The sooner you go solar, the sooner you can lock in your current rate and opt out of energy cost inflation.',
    iconUrl: iconRising,
  },
  {
    title: 'Spend less on energy',
    description:
      'Home solar can cut your energy bill and now, if you qualify, your tax bill. Learn more about the 30% tax credits included in the new Inflation Reduction Act.²',
    iconUrl: iconCredit,
  },
  {
    title: 'Solar can increase the value of your home',
    description:
      'An analysis by Zillow³ showed that purchasing a solar system can increase the value of your home by as much as 4%.',
    iconUrl: iconValue,
  },
];

export const goSolarReasonsDebranded = [
  {
    title: 'Electric bills are rising',
    description:
      'The average residential electricity rate is projected to increase 7.5% in the United States in 2022.¹ The sooner you go solar, the sooner you can lock in your current rate and opt out of energy cost inflation.',
    iconUrl: iconRising,
  },
  {
    title: 'Spend Less On Energy',
    description:
      'Home solar can cut your energy bill and now, if you qualify, your tax bill. Learn more about the 30% tax credits included in the new Inflation Reduction Act.²',
    iconUrl: iconCredit,
  },
  {
    title: 'Solar can increase the value of your home',
    description:
      'An analysis by Zillow³ showed that purchasing a solar system can increase the value of your home by as much as 4%.',
    iconUrl: iconValue,
  },
];

export const faqs = [
  {
    question: 'Why should I consider a home solar system?',
    answer:
      'There are millions of solar homes in the United States, and people go solar for many different reasons. Commonly, the reason is to reduce the amount they pay for energy, but many also go solar to run their homes on clean energy. According to the U.S. Department of Energy, “A solar electric system provides an opportunity for anyone who is looking to reduce monthly utility bills and make a long-term, low-risk investment.”¹',
    footNote:
      '¹ https://www.energy.gov/energysaver/benefits-residential-solar-electricity',
  },
  {
    question: 'How can I purchase a home solar system?',
    answer:
      'There are several ways to go solar, but the best way is to work with a solar installation partner. A skilled solar installation partner can not only help plan your solar system, but also get the right parts, file the appropriate permits, and safely put a solar system on your home. If you are not planning on paying in cash, many solar installation partners have relationships with solar financing companies that can help you with a solar loan.',
  },
  {
    question: 'What products make up a home solar system?',
    answer:
      'In addition to your home’s electrical panel and meter, you need solar panels and microinverters to make a basic solar system. Solar panels turn sunlight into electricity, and microinverters make that electricity usable for homes. There are many other components a qualified solar installation partner can help you consider, such as devices that allow you to see how much energy you’re making in real time, or switches that make it possible to use your solar power during a grid outage.',
  },
  {
    question: 'Will my solar system work in a grid outage?',
    answer:
      'There are broadly two kinds of solar systems — “grid-tied” systems and “grid-agnostic” systems. Grid-tied systems let you use your solar power only when the grid is up and running. If the grid fails, your solar power cannot be used. This is a safety feature to keep electricity from going back onto the grid while people are trying to repair it. Grid-agnostic systems have a switch that can cut a home off from the grid temporarily, allowing you to use your solar power in an outage. Most of the time these systems are solar-plus-battery systems, but recently there have been solar-only grid-agnostic systems entering the market.',
  },
  {
    question: 'When is the best time to go solar?',
    answer:
      'To get the most savings out of a solar system, it makes sense to start as soon as you can. In many places, solar can be installed year-round.',
  },
  {
    question: 'How much does a home solar system cost?',
    answer: `<p>First, it helps to understand that solar systems are priced in “dollars per watt.” Solar panels have wattage ratings, and an overall system size is measured based on the total of those solar panels. For example, 20 solar panels rated at 250 watts each would be a 5-kilowatt (5,000 watts) system — which happens to be the average solar system size in the United States, according to the Solar Energy Industries Association (SEIA). <sup>2</sup> If you take the cost of installing a system — including installation, microinverters, and other equipment — and divide that cost by 5kW, you get the dollars per watt.
    <br/>
    <br />
    According to the SEIA,<sup>3</sup> the average price of a solar system in 2020 and 2021 has been around $3/watt. Using that, a 5kW system at $3/watt — an average-sized system, at an average price — would cost $15,000.
    <br/>
    <br />
    Talking to a solar installation partner is the best way to understand prices. There are a variety of other factors that influence price, such as which components you choose. But there can also be prices outside of the system itself, such as if a roof needs repairs, or if a new electrical panel is required.</p>`,
    footNote: `<p><sup>2</sup> https://www.energy.gov/energysaver/benefits-residential-solar-electricity<br/><sup>3</sup> https://www.seia.org/solar-industry-research-data</p>`,
  },
  {
    question: 'How long does a home solar system last?',
    answer:
      'Solar systems should last decades. Many major solar panel manufacturers have production warranties at 25 years. Some microinverter companies also have warranties at 25 years. The best resource for understanding the lifetime value of a solar system is a solar installation partner, who will put together a proposal with products and know the warranties and expected life of the system.',
  },
  {
    question: 'What incentives are available for a home solar system?',
    answer:
      '<p>The largest incentive for installing home solar is the Federal Investment Tax Credit (ITC), or, according to the U.S. Department of Energy,<sup>4</sup> “a dollar-for-dollar reduction in the amount of income tax you would otherwise owe.” As of September 1, 2022, the Federal ITC provides a 30% tax credit for installed solar. There are often state and local incentives<sup>5</sup> as well, and your tax advisor can help you understand those incentives, and even help in claiming them.</p>',
    footNote:
      '<p><sup>4</sup> https://www.energy.gov/eere/solar/homeowners-guide-federal-tax-credit-solar-photovoltaics<br/><sup>5</sup> https://www.dsireusa.org/</p>',
  },
  {
    question: 'Will a home solar system change the value of my home?',
    answer:
      '<p>According to the U.S. Department of Energy, “purchasing a solar energy system will likely increase your home’s value.”<sup>6</sup> Studies cited by the Department of Energy showed, on average, “solar increased the value of a home by about $15,000.”</p>',
    footNote:
      '<p><sup>6</sup> https://www.energy.gov/energysaver/benefits-residential-solar-electricity</p>',
  },
  {
    question: 'Why do some homeowners add batteries to their solar system?',
    answer:
      '<p>The Solar Energy Industries Association predicts that more than 29% of systems installed in 2025 will have battery attached, up from roughly 10% in 2021.<sup>7</sup> Homeowners are opting for battery for several reasons. One is to have power in the home when the grid fails. Traditional “grid-tied” solar systems can’t power a home when the grid fails. Others opt for battery to save extra solar power made during the day to use at night.</p>',
    footNote:
      '<p><sup>7</sup> https://www.seia.org/solar-industry-research-data</p>',
  },
  {
    question:
      'Does solar only work in sunny places? Will it work in colder climates?',
    answer:
      '<p>According to the U.S. Department of Energy, “Most states in the United States have good-to-excellent solar resources. Even places with relatively low solar resources, such as the Pacific Northwest and Alaska, can experience cost savings.”<sup>8</sup></p>',
    footNote:
      '<p><sup>8</sup> https://www.energy.gov/energysaver/benefits-residential-solar-electricity</p>',
  },
  {
    question: 'How does home solar save money for homeowners?',
    answer:
      'How much money can be saved depends on a variety of factors, such as how much electricity you consume; the size and price of your solar system; if it was purchased with cash, with a loan, or through a lease; and your local electricity rates. A solar installation partner can help you understand all these factors and can help you estimate how much money you’ll be able to save.',
  },
  {
    question: 'What financial options are available for going solar?',
    answer:
      'There are several ways to go solar. One is to buy a solar system outright, in the same way you would pay for a car in cash. You can also finance a solar system with a loan. The advantage to the former is avoiding interest payments. Some solar companies also offer leases, or Power Purchase Agreements, in which the solar company owns the solar system on your home, and you pay a preset price for the energy it produces. A solar installation partner can help you understand your options, and many have relationships with solar financing partners.',
  },
  {
    question: 'If I drive an EV, can I charge it with my own solar power?',
    answer:
      'As more drivers adopt electric vehicles, the prospect of going solar gets even better. A car plugged in and charging during the day at a solar home is fueling up on sunshine, which is cheaper and cleaner than other automotive fuel sources.',
  },
  {
    question: 'What are virtual power plants and how can I participate in one?',
    answer:
      '<p>Virtual power plants (VPPs) are networks that aggregate and manage decentralized energy resources, such as solar panels and batteries, to enhance grid reliability and efficiency by centrally coordinating their operation. Participants can join a VPP by connecting their renewable energy systems to a central platform, allowing coordinated control of energy generation and storage for grid stability and optimization.<sup>9</sup></p>',
    footNote:
      '<p><sup>9</sup> https://enphase.com/installers/grid-services</p>',
  },
];
